import { motion } from 'framer-motion';
import { Receipt, Info } from 'lucide-react';
import { OrderPricing } from '@/types/order';
import { formatPrice } from '@/hooks/useOrderPricing';

interface PricingSummaryProps {
  pricing: OrderPricing;
  className?: string;
}

export function PricingSummary({ pricing, className = '' }: PricingSummaryProps) {
  const { items, supplementVisites, extraVisitesCount, total, hasQuoteItems } = pricing;

  if (items.length === 0) {
    return (
      <div className={`bg-white rounded-2xl border-2 border-navy-100 p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gold-100 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-gold-600" />
          </div>
          <h3 className="text-lg font-semibold text-navy-600">Récapitulatif</h3>
        </div>
        <p className="text-sm text-navy-400 text-center py-8">
          Sélectionnez des services pour voir le récapitulatif
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl border-2 border-gold-500 shadow-lg ${className}`}
    >
      {/* Header */}
      <div className="bg-gold-50 px-6 py-4 rounded-t-2xl border-b border-gold-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
            <Receipt className="w-5 h-5 text-gold-600" />
          </div>
          <h3 className="text-lg font-semibold text-navy-600">Récapitulatif</h3>
        </div>
      </div>

      {/* Items */}
      <div className="px-6 py-4 space-y-3">
        {items.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-start justify-between gap-4"
          >
            <span className="text-sm text-navy-600 flex-1">
              {item.label}
            </span>
            <span className="text-sm font-semibold text-navy-600 whitespace-nowrap">
              {item.isQuote ? (
                <span className="text-xs px-2 py-1 bg-navy-100 text-navy-600 rounded">
                  Sur devis
                </span>
              ) : (
                `${item.price} €`
              )}
            </span>
          </motion.div>
        ))}

        {/* Separator */}
        {items.length > 0 && (
          <div className="border-t border-navy-100 my-4" />
        )}

        {/* Supplément visites */}
        {supplementVisites > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-start justify-between gap-4"
          >
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-navy-600">
                Supplément visites
              </span>
              <div className="group relative">
                <Info className="w-3.5 h-3.5 text-navy-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                  <div className="bg-navy-600 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                    {extraVisitesCount} visite{extraVisitesCount > 1 ? 's' : ''} hors forfait
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-navy-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <span className="text-sm font-semibold text-amber-600 whitespace-nowrap">
              +{supplementVisites} €
            </span>
          </motion.div>
        )}
      </div>

      {/* Footer with Total */}
      <div className="bg-gold-50 px-6 py-4 rounded-b-2xl border-t border-gold-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-base font-semibold text-navy-600">
            Total estimé
          </span>
          <span className="text-2xl font-bold text-gold-600">
            {formatPrice(total)}
          </span>
        </div>

        {hasQuoteItems && (
          <p className="text-xs text-navy-400 mt-2">
            * Hors services sur devis
          </p>
        )}

        <p className="text-xs text-navy-400">
          HTVA
        </p>
      </div>
    </motion.div>
  );
}

// Version mobile collapsible (sticky bottom)
export function PricingSummaryMobile({ pricing }: { pricing: OrderPricing }) {
  const { total, hasQuoteItems } = pricing;

  if (pricing.items.length === 0) return null;

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gold-500 shadow-2xl z-40 lg:hidden"
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-navy-400">Total estimé</p>
            <p className="text-2xl font-bold text-gold-600">
              {formatPrice(total)}
            </p>
            {hasQuoteItems && (
              <p className="text-xs text-navy-400">
                * Hors services sur devis
              </p>
            )}
          </div>
          <Receipt className="w-8 h-8 text-gold-600" />
        </div>
      </div>
    </motion.div>
  );
}
